import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, of } from 'rxjs';
import { takeUntil, timeout, catchError } from 'rxjs/operators';
import { LocalAgentService, LocalAgentCertificate, CertificationPathItem } from '../../core/services/local-agent.service';
import { environment } from '../../../environments/environment';

const CERT_REQUEST_TIMEOUT = (environment.localAgentHealthTimeout ?? 5000) + 2000;

@Component({
  selector: 'app-almacen-certificados',
  templateUrl: './almacen-certificados.component.html',
  styleUrls: ['./almacen-certificados.component.css'],
  standalone: false
})
export class AlmacenCertificadosComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  loading = false;
  agentUnavailable = false;
  certificates: LocalAgentCertificate[] = [];

  /** Índice de pestaña activa por certificado: 0 General, 1 Titular y emisor, 2 Técnico, 3 Ruta de certificación */
  activeTabs: number[] = [];
  /** Nodo de la ruta seleccionado para ver detalle (por índice de certificado). */
  selectedPathNodeByCertIndex: (CertificationPathItem | null)[] = [];

  constructor(private localAgentService: LocalAgentService) {}

  ngOnInit(): void {
    this.loadCertificates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasCertificates(): boolean {
    return this.certificates.length > 0;
  }

  get showNotAvailableView(): boolean {
    return this.loading || this.agentUnavailable;
  }

  get showEmptyState(): boolean {
    return !this.loading && !this.agentUnavailable && this.certificates.length === 0;
  }

  get showCertificateList(): boolean {
    return !this.loading && !this.agentUnavailable && this.hasCertificates;
  }

  loadCertificates(): void {
    this.loading = true;
    this.agentUnavailable = false;
    this.certificates = [];

    this.localAgentService.getCertificates().pipe(
      timeout(CERT_REQUEST_TIMEOUT),
      takeUntil(this.destroy$),
      catchError(() => {
        this.agentUnavailable = true;
        this.loading = false;
        return of([]);
      })
    ).subscribe({
      next: (list) => {
        this.certificates = list ?? [];
        this.loading = false;
      },
      error: () => {
        this.agentUnavailable = true;
        this.loading = false;
      }
    });
  }

  getActiveTab(certIndex: number): number {
    return this.activeTabs[certIndex] ?? 0;
  }

  setActiveTab(certIndex: number, tab: number): void {
    const next = [...this.activeTabs];
    next[certIndex] = tab;
    this.activeTabs = next;
  }

  displayName(cert: LocalAgentCertificate): string {
    return cert.friendlyName || this.extractCN(cert.subjectDN) || cert.thumbprint || 'Certificado';
  }

  extractCN(dn?: string): string | null {
    if (!dn) return null;
    const match = dn.match(/CN=([^,]+)/i);
    return match ? match[1].trim() : null;
  }

  extractIssuerName(issuerDN?: string): string {
    if (!issuerDN) return '';
    const cnMatch = issuerDN.match(/CN=([^,]+)/i);
    const oMatch = issuerDN.match(/O=([^,]+)/i);
    const cn = cnMatch ? cnMatch[1].trim() : '';
    const o = oMatch ? oMatch[1].trim() : '';
    if (cn && o) return `${cn} - ${o}`;
    return cn || o || issuerDN;
  }

  /**
   * Parsea un DN (Subject o Issuer) separando por ", " y cada parte en clave=valor.
   */
  parseDN(dn?: string): { key: string; value: string }[] {
    if (!dn || !dn.trim()) return [];
    const parts = dn.split(/,\s*/).map(p => p.trim()).filter(Boolean);
    const result: { key: string; value: string }[] = [];
    for (const part of parts) {
      const eq = part.indexOf('=');
      if (eq >= 0) {
        result.push({
          key: part.substring(0, eq).trim(),
          value: part.substring(eq + 1).trim()
        });
      } else {
        result.push({ key: '', value: part });
      }
    }
    return result;
  }

  formatDate(dateStr?: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-CO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  }

  isExpired(notAfter?: string): boolean {
    if (!notAfter) return false;
    try {
      return new Date(notAfter) < new Date();
    } catch {
      return false;
    }
  }

  get agentDownloadUrl(): string {
    return environment.agentDownloadUrl ?? '#';
  }

  /**
   * Ruta de certificación ordenada raíz arriba (índice 0 = raíz, último = titular).
   */
  getCertificationPathOrdered(cert: LocalAgentCertificate): CertificationPathItem[] {
    const path = cert.certificationPath;
    if (!path || path.length === 0) return [];
    return [...path].reverse();
  }

  isTitularInPath(pathOrdered: CertificationPathItem[], pathIdx: number): boolean {
    return pathOrdered.length > 0 && pathIdx === pathOrdered.length - 1;
  }

  selectPathNode(certIndex: number, node: CertificationPathItem | null): void {
    const next = [...this.selectedPathNodeByCertIndex];
    next[certIndex] = node;
    this.selectedPathNodeByCertIndex = next;
  }

  getSelectedPathNode(certIndex: number): CertificationPathItem | null {
    return this.selectedPathNodeByCertIndex[certIndex] ?? null;
  }
}
